import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ResearchNote, ResearchType, ResearchStatus, Priority, User, ResearchTask, SourceCitation } from '../../types';
import { ResearchNoteService } from '../../services/researchNoteService';

interface ResearchEditorProps {
  personId: string;
  personName: string;
  currentUser: User;
  existingNote: ResearchNote | null;
  onSave: (note: ResearchNote) => void;
  onCancel?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ResearchEditor: React.FC<ResearchEditorProps> = ({
  personId,
  personName,
  currentUser,
  existingNote,
  onSave,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Basic fields
  const [title, setTitle] = useState(existingNote?.title || '');
  const [type, setType] = useState<ResearchType>(existingNote?.type || ResearchType.OTHER);
  const [objective, setObjective] = useState(existingNote?.objective || '');
  const [findings, setFindings] = useState(existingNote?.findings || '');
  const [status, setStatus] = useState<ResearchStatus>(existingNote?.status || ResearchStatus.NOT_STARTED);
  const [priority, setPriority] = useState<Priority>(existingNote?.priority || Priority.MEDIUM);
  const [isPrivate, setIsPrivate] = useState(existingNote?.isPrivate ?? true);

  // Tasks
  const [tasks, setTasks] = useState<ResearchTask[]>(existingNote?.tasks || []);
  const [newTaskDescription, setNewTaskDescription] = useState('');

  // Sources
  const [sources, setSources] = useState<SourceCitation[]>(existingNote?.sources || []);
  const [newSourceTitle, setNewSourceTitle] = useState('');
  const [newSourceType, setNewSourceType] = useState<'book' | 'website' | 'document' | 'database' | 'interview' | 'other'>('website');
  const [newSourceAuthor, setNewSourceAuthor] = useState('');
  const [newSourceURL, setNewSourceURL] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle add task
  const handleAddTask = useCallback(() => {
    if (!newTaskDescription.trim()) return;

    const newTask: ResearchTask = {
      id: `task_${Date.now()}`,
      description: newTaskDescription.trim(),
      isCompleted: false,
    };

    setTasks(prev => [...prev, newTask]);
    setNewTaskDescription('');
  }, [newTaskDescription]);

  // Handle toggle task
  const handleToggleTask = useCallback((taskId: string) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, isCompleted: !task.isCompleted } : task
    ));
  }, []);

  // Handle delete task
  const handleDeleteTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  // Handle add source
  const handleAddSource = useCallback(() => {
    if (!newSourceTitle.trim()) {
      setError('Source title is required');
      return;
    }

    const newSource: SourceCitation = {
      id: `source_${Date.now()}`,
      sourceType: newSourceType,
      title: newSourceTitle.trim(),
      author: newSourceAuthor.trim() || undefined,
      url: newSourceURL.trim() || undefined,
    };

    setSources(prev => [...prev, newSource]);
    setNewSourceTitle('');
    setNewSourceAuthor('');
    setNewSourceURL('');
    setError(null);
  }, [newSourceTitle, newSourceType, newSourceAuthor, newSourceURL]);

  // Handle delete source
  const handleDeleteSource = useCallback((sourceId: string) => {
    setSources(prev => prev.filter(source => source.id !== sourceId));
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setError('Please enter a title');
      setActiveTab(0);
      return;
    }

    if (!objective.trim()) {
      setError('Please enter a research objective');
      setActiveTab(0);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let savedNote: ResearchNote;

      const noteData = {
        personId,
        title: title.trim(),
        type,
        objective: objective.trim(),
        findings: findings.trim() || undefined,
        status,
        priority,
        isPrivate,
        tasks,
        sources,
      };

      if (existingNote?.id) {
        // Update existing note
        await ResearchNoteService.updateResearchNote(
          existingNote.id,
          noteData,
          currentUser.uid,
          currentUser.displayName,
          currentUser.role
        );
        // Refetch updated note
        const updatedNote = await ResearchNoteService.getResearchNoteById(existingNote.id, currentUser.uid);
        if (!updatedNote) throw new Error('Failed to retrieve updated research note');
        savedNote = updatedNote;
      } else {
        // Create new note
        savedNote = await ResearchNoteService.createResearchNote(
          noteData,
          currentUser.uid,
          currentUser.displayName,
          currentUser.role
        );
      }

      // Call completion handler
      onSave(savedNote);

      // Reset form
      setTitle('');
      setType(ResearchType.OTHER);
      setObjective('');
      setFindings('');
      setStatus(ResearchStatus.NOT_STARTED);
      setPriority(Priority.MEDIUM);
      setIsPrivate(true);
      setTasks([]);
      setSources([]);
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save research note');
    } finally {
      setIsSaving(false);
    }
  }, [title, type, objective, findings, status, priority, isPrivate, tasks, sources, personId, personName, currentUser, existingNote, onSave]);

  return (
    <Box>
      <DialogTitle>
        {existingNote ? 'Edit Research Note' : 'Create Research Note'}
        {onCancel && (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Error message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Details" />
          <Tab label="Tasks" />
          <Tab label="Sources" />
        </Tabs>

        {/* Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Stack spacing={3}>
            {/* Info message */}
            <Alert severity="info">
              {isPrivate
                ? `Create a private research note for ${personName}. Only you and assigned collaborators can see it.`
                : `Create a shared research note for ${personName}. Other users can view it after approval.`}
            </Alert>

            {/* Private/Shared toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={!isPrivate}
                  onChange={(e) => setIsPrivate(!e.target.checked)}
                  disabled={isSaving}
                />
              }
              label={isPrivate ? 'Private Research' : 'Shared Research'}
            />

            {/* Title */}
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              disabled={isSaving}
              helperText="Brief, descriptive title for this research"
            />

            {/* Research Type */}
            <FormControl fullWidth required disabled={isSaving}>
              <InputLabel>Research Type</InputLabel>
              <Select value={type} label="Research Type" onChange={(e) => setType(e.target.value as ResearchType)}>
                <MenuItem value="vital-records">Vital Records</MenuItem>
                <MenuItem value="census">Census</MenuItem>
                <MenuItem value="military">Military</MenuItem>
                <MenuItem value="immigration">Immigration</MenuItem>
                <MenuItem value="land-records">Land Records</MenuItem>
                <MenuItem value="probate">Probate</MenuItem>
                <MenuItem value="church-records">Church Records</MenuItem>
                <MenuItem value="newspapers">Newspapers</MenuItem>
                <MenuItem value="city-directories">City Directories</MenuItem>
                <MenuItem value="employment">Employment</MenuItem>
                <MenuItem value="dna">DNA</MenuItem>
                <MenuItem value="photographs">Photographs</MenuItem>
                <MenuItem value="oral-history">Oral History</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Objective */}
            <TextField
              label="Research Objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              disabled={isSaving}
              helperText="What are you trying to discover or verify?"
            />

            {/* Findings */}
            <TextField
              label="Findings"
              value={findings}
              onChange={(e) => setFindings(e.target.value)}
              fullWidth
              multiline
              rows={4}
              disabled={isSaving}
              helperText="Document what you've discovered so far"
            />

            {/* Status and Priority */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required disabled={isSaving}>
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value as ResearchStatus)}>
                  <MenuItem value="not-started">Not Started</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="on-hold">On Hold</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth required disabled={isSaving}>
                <InputLabel>Priority</InputLabel>
                <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value as Priority)}>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={activeTab} index={1}>
          <Stack spacing={2}>
            {/* Add task form */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Add Research Task
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                  placeholder="Task description..."
                  disabled={isSaving}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddTask}
                  disabled={!newTaskDescription.trim() || isSaving}
                >
                  Add
                </Button>
              </Stack>
            </Paper>

            {/* Task list */}
            {tasks.length === 0 ? (
              <Alert severity="info">
                No tasks yet. Add tasks to break down your research into manageable steps.
              </Alert>
            ) : (
              <Paper variant="outlined">
                <List>
                  {tasks.map((task, index) => (
                    <ListItem
                      key={task.id}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleDeleteTask(task.id)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                      divider={index < tasks.length - 1}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={task.isCompleted}
                          onChange={() => handleToggleTask(task.id)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.description}
                        sx={{
                          textDecoration: task.isCompleted ? 'line-through' : 'none',
                          opacity: task.isCompleted ? 0.6 : 1,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary">
                    {tasks.filter(t => t.isCompleted).length} of {tasks.length} tasks completed
                  </Typography>
                </Box>
              </Paper>
            )}
          </Stack>
        </TabPanel>

        {/* Sources Tab */}
        <TabPanel value={activeTab} index={2}>
          <Stack spacing={2}>
            {/* Add source form */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Add Source Citation
              </Typography>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Source Type</InputLabel>
                  <Select
                    value={newSourceType}
                    label="Source Type"
                    onChange={(e) => setNewSourceType(e.target.value as any)}
                  >
                    <MenuItem value="book">Book</MenuItem>
                    <MenuItem value="website">Website</MenuItem>
                    <MenuItem value="document">Document</MenuItem>
                    <MenuItem value="database">Database</MenuItem>
                    <MenuItem value="interview">Interview</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  value={newSourceTitle}
                  onChange={(e) => setNewSourceTitle(e.target.value)}
                  placeholder="Source title *"
                  fullWidth
                />

                <TextField
                  size="small"
                  value={newSourceAuthor}
                  onChange={(e) => setNewSourceAuthor(e.target.value)}
                  placeholder="Author (optional)"
                  fullWidth
                />

                <TextField
                  size="small"
                  value={newSourceURL}
                  onChange={(e) => setNewSourceURL(e.target.value)}
                  placeholder="URL (optional)"
                  fullWidth
                />

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddSource}
                  disabled={!newSourceTitle.trim() || isSaving}
                >
                  Add Source
                </Button>
              </Stack>
            </Paper>

            {/* Source list */}
            {sources.length === 0 ? (
              <Alert severity="info">
                No sources yet. Add citations for the sources you've consulted.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {sources.map((source) => (
                  <Paper key={source.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Chip label={source.sourceType} size="small" />
                          <Typography variant="subtitle2">
                            {source.title}
                          </Typography>
                        </Stack>
                        {source.author && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Author: {source.author}
                          </Typography>
                        )}
                        {source.url && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            URL: {source.url}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleDeleteSource(source.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || !objective.trim() || isSaving}
        >
          {isSaving ? 'Saving...' : existingNote ? 'Update Research Note' : 'Create Research Note'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default ResearchEditor;
